import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import StudentLayout from '@/components/layouts/StudentLayout';
import { 
  ArrowLeft, ArrowRight, Volume2, CheckCircle, Loader2, 
  Grid3X3, ListOrdered, PenTool, Lightbulb
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const TimesTablesLearnPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { tableNumber } = useParams();
  const { api, user } = useAuth();
  const isAdminPreview = location.pathname.includes('/admin/preview');
  
  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentFact, setCurrentFact] = useState(0);
  const [mode, setMode] = useState('arrays'); // arrays, skip_counting, practice
  const [practiceAnswer, setPracticeAnswer] = useState('');
  const [practiceResults, setPracticeResults] = useState({});
  const [audioLoading, setAudioLoading] = useState(false);
  const audioRef = useRef(null);

  const getBasePath = () => isAdminPreview ? '/admin/preview/times-tables' : '/student/times-tables';
  const tableNum = parseInt(tableNumber);

  useEffect(() => {
    fetchLesson();
  }, [tableNumber]);

  const fetchLesson = async () => {
    try {
      const response = await api.get(`/v2/times-tables/lesson/${tableNumber}`);
      setLesson(response.data);
    } catch (error) {
      console.error('Error fetching lesson:', error);
      toast.error('Failed to load lesson');
      navigate(getBasePath());
    } finally {
      setLoading(false);
    }
  };

  const speakFact = async (text) => {
    setAudioLoading(true);
    try {
      const response = await api.post('/v2/media/tts/generate', { text, voice: 'nova' });
      if (response.data.audio) {
        const audioSrc = `data:audio/mp3;base64,${response.data.audio}`;
        audioRef.current.src = audioSrc;
        audioRef.current.play();
      }
    } catch (error) {
      console.error('TTS error:', error);
    } finally {
      setAudioLoading(false);
    }
  };

  const handlePracticeSubmit = async () => {
    const fact = lesson.facts[currentFact];
    const isCorrect = parseInt(practiceAnswer) === fact.answer;
    
    setPracticeResults(prev => ({
      ...prev,
      [currentFact]: isCorrect
    }));

    // Record attempt
    if (user?.id && !isAdminPreview) {
      try {
        await api.post(`/v2/times-tables/progress/${user.id}/record-attempt`, {
          table_number: tableNum,
          question: fact.equation,
          correct_answer: fact.answer,
          user_answer: parseInt(practiceAnswer) || 0,
          is_correct: isCorrect,
          time_taken_ms: 0
        });
      } catch (error) {
        console.error('Failed to record attempt:', error);
      }
    }

    if (isCorrect) {
      toast.success('Correct! 🎉');
      if (currentFact < 11) {
        setTimeout(() => {
          setCurrentFact(prev => prev + 1);
          setPracticeAnswer('');
        }, 1000);
      } else {
        toast.success('Amazing! You completed the practice! ⭐');
        // Record lesson complete
        if (user?.id && !isAdminPreview) {
          try {
            await api.post(`/v2/times-tables/progress/${user.id}/record-lesson`, {
              table_number: tableNum,
              lesson_type: 'practice'
            });
          } catch (error) {
            console.error('Failed to record lesson:', error);
          }
        }
      }
    } else {
      toast.error(`Not quite! The answer is ${fact.answer}`);
    }
  };

  const renderArrayVisual = (rows, cols) => {
    return (
      <div className="flex flex-col gap-1 items-center">
        {Array(Math.min(rows, 12)).fill(0).map((_, rowIndex) => (
          <div key={rowIndex} className="flex gap-1">
            {Array(Math.min(cols, 12)).fill(0).map((_, colIndex) => (
              <div 
                key={colIndex}
                className="w-6 h-6 md:w-8 md:h-8 bg-gradient-to-br from-purple-400 to-indigo-500 rounded-full shadow-sm"
              />
            ))}
          </div>
        ))}
      </div>
    );
  };

  const renderSkipCounting = (skipCount) => {
    return (
      <div className="flex flex-wrap gap-2 justify-center">
        {skipCount.map((num, index) => (
          <div 
            key={index}
            className="w-12 h-12 md:w-14 md:h-14 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-xl flex items-center justify-center text-white font-bold text-lg shadow-md"
          >
            {num}
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <StudentLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-10 h-10 animate-spin text-purple-600" />
        </div>
      </StudentLayout>
    );
  }

  const fact = lesson?.facts?.[currentFact];

  return (
    <StudentLayout>
      <audio ref={audioRef} />
      <div data-testid="times-tables-learn" className="max-w-4xl mx-auto">
        {/* Back Button */}
        <button 
          onClick={() => navigate(getBasePath())}
          className="flex items-center gap-2 text-slate-600 hover:text-purple-600 font-medium mb-6"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Times Tables
        </button>

        {/* Header */}
        <div className="student-card bg-gradient-to-r from-blue-500 to-indigo-600 text-white mb-6">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
              <span className="text-3xl font-bold">{tableNum}×</span>
            </div>
            <div>
              <h1 className="font-heading text-2xl font-bold">Learn the {tableNum} Times Table</h1>
              <p className="text-blue-100">{lesson?.tip}</p>
            </div>
          </div>
        </div>

        {/* Mode Tabs */}
        <div className="flex gap-2 mb-6">
          <Button
            variant={mode === 'arrays' ? 'default' : 'outline'}
            onClick={() => setMode('arrays')}
            className={mode === 'arrays' ? 'bg-purple-600' : ''}
          >
            <Grid3X3 className="w-4 h-4 mr-2" />
            Visual Arrays
          </Button>
          <Button
            variant={mode === 'skip_counting' ? 'default' : 'outline'}
            onClick={() => setMode('skip_counting')}
            className={mode === 'skip_counting' ? 'bg-emerald-600' : ''}
          >
            <ListOrdered className="w-4 h-4 mr-2" />
            Skip Counting
          </Button>
          <Button
            variant={mode === 'practice' ? 'default' : 'outline'}
            onClick={() => setMode('practice')}
            className={mode === 'practice' ? 'bg-amber-600' : ''}
          >
            <PenTool className="w-4 h-4 mr-2" />
            Practice
          </Button>
        </div>

        {/* Progress Indicator */}
        <div className="mb-4 flex items-center gap-2">
          <span className="text-sm text-slate-500">Fact {currentFact + 1} of 12</span>
          <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-purple-500 transition-all duration-300"
              style={{ width: `${((currentFact + 1) / 12) * 100}%` }}
            />
          </div>
        </div>

        {/* Content Area */}
        <div className="student-card min-h-[400px]">
          {/* Equation Display */}
          <div className="text-center mb-8">
            <div className="text-5xl md:text-6xl font-bold text-slate-800 mb-2">
              {fact?.equation} = {mode === 'practice' ? '?' : fact?.answer}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => speakFact(`${tableNum} times ${fact?.multiplier} equals ${fact?.answer}`)}
              disabled={audioLoading}
            >
              <Volume2 className={`w-5 h-5 mr-2 ${audioLoading ? 'animate-pulse' : ''}`} />
              Listen
            </Button>
          </div>

          {/* Mode Content */}
          {mode === 'arrays' && (
            <div className="space-y-4">
              <p className="text-center text-slate-600 mb-4">
                {tableNum} rows × {fact?.multiplier} columns = {fact?.answer} dots
              </p>
              <div className="flex justify-center">
                {renderArrayVisual(tableNum, fact?.multiplier)}
              </div>
            </div>
          )}

          {mode === 'skip_counting' && (
            <div className="space-y-4">
              <p className="text-center text-slate-600 mb-4">
                Count by {tableNum}s to reach {fact?.answer}:
              </p>
              {renderSkipCounting(fact?.skip_count)}
            </div>
          )}

          {mode === 'practice' && (
            <div className="space-y-4">
              <p className="text-center text-slate-600 mb-4">
                What is {fact?.equation}?
              </p>
              <div className="flex justify-center gap-4 items-center">
                <input
                  type="number"
                  value={practiceAnswer}
                  onChange={(e) => setPracticeAnswer(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handlePracticeSubmit()}
                  className="w-32 text-center text-3xl font-bold border-2 border-purple-300 rounded-xl p-4 focus:border-purple-500 focus:outline-none"
                  placeholder="?"
                  autoFocus
                  data-testid="practice-input"
                />
                <Button 
                  onClick={handlePracticeSubmit}
                  className="bg-purple-600 hover:bg-purple-700"
                  disabled={!practiceAnswer}
                >
                  Check
                </Button>
              </div>
              {practiceResults[currentFact] !== undefined && (
                <div className={`text-center text-lg font-medium ${practiceResults[currentFact] ? 'text-emerald-600' : 'text-red-500'}`}>
                  {practiceResults[currentFact] ? '✓ Correct!' : `✗ The answer is ${fact?.answer}`}
                </div>
              )}
            </div>
          )}

          {/* Tip Box */}
          <div className="mt-8 p-4 bg-amber-50 rounded-xl border border-amber-200">
            <div className="flex items-start gap-3">
              <Lightbulb className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800">{lesson?.tip}</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between mt-6">
          <Button
            variant="outline"
            onClick={() => setCurrentFact(prev => Math.max(0, prev - 1))}
            disabled={currentFact === 0}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Previous
          </Button>
          
          {currentFact < 11 ? (
            <Button
              onClick={() => setCurrentFact(prev => prev + 1)}
              className="bg-purple-600 hover:bg-purple-700"
            >
              Next
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={() => navigate(getBasePath())}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Complete
            </Button>
          )}
        </div>
      </div>
    </StudentLayout>
  );
};

export default TimesTablesLearnPage;
